import React, { useEffect, useRef, useState } from 'react';
import { Map as LeafletMap, Polygon, ZoomControl } from 'react-leaflet';
import L, { LatLngBoundsExpression, LatLngExpression } from 'leaflet';
import { useSelector } from 'react-redux';
import { ApplicationState } from '../../../store';
import { TrafficAreaEnum } from '../../../models/tariff/trafficAreas/TrafficAreaEnum';
import { Position } from 'geojson';
import { SPLIT_COORDINATE } from '../../../config';
import 'leaflet/dist/leaflet.css';
import LeafletBaseLayerToggle, { LeafletBaseLayer, LeafletLabelsState } from './LeafletBaseLayerToggle';
import LeafletBaseLayers from './LeafletBaseLayers';
import LeafletFullscreenControl from './LeafletFullscreenControl';

const containerStyle: React.CSSProperties = {
    width: '100%',
    height: '260px',
    border: '1px solid transparent',
    borderRadius: '15px'
};

const containerStyleFullView: React.CSSProperties = {
    width: '100%',
    height: '520px'
};

const polygonStrokeColor = '#102A83';
const polygonFillColor = 'rgba(58, 114, 255, 1)';

interface Props {
    trafficAreas: number[];
    fullView?: boolean;
}

interface LatLng {
    latitude: number;
    longitude: number;
}

type Coordinates = LatLng[];

const isValidLatLng = (coordinate: LatLng): boolean =>
    Number.isFinite(coordinate.latitude) && Number.isFinite(coordinate.longitude);

const isValidLatLngExpression = (point: LatLngExpression): boolean => {
    if (!Array.isArray(point))
        return false;

    const [latitude, longitude] = point;

    return Number.isFinite(latitude) && Number.isFinite(longitude);
};

function BoundsController({ map, polygons }: { map: L.Map | null; polygons: LatLngExpression[][][] }) {
    useEffect(() => {
        if (!map)
            return;

        const flyToVisibleBounds = () => {
            if (!polygons.length)
                return;

            const mapSize = map.getSize();
            if (!mapSize.x || !mapSize.y)
                return;

            const allPoints: LatLngExpression[] = polygons.flat(2).filter(isValidLatLngExpression);
            if (!allPoints.length)
                return;

            const bounds = allPoints as LatLngBoundsExpression;
            map.invalidateSize();
            map.flyToBounds(bounds, { duration: 1 });
        };

        flyToVisibleBounds();
        window.addEventListener('resize', flyToVisibleBounds);

        return () => window.removeEventListener('resize', flyToVisibleBounds);
    }, [map, polygons]);

    return null;
}

function MapJLSLeaflet(props: Props) {
    const [leafletPolygons, setLeafletPolygons] = useState<LatLngExpression[][][]>([]);
    const [activeBaseLayer, setActiveBaseLayer] = useState<LeafletBaseLayer>('map');
    const [labelsByLayer, setLabelsByLayer] = useState<LeafletLabelsState>({ map: true, satellite: true });
    const [mapInstance, setMapInstance] = useState<L.Map | null>(null);
    const mapWrapperRef = useRef<HTMLDivElement>(null);

    const trafficAreasGeography = useSelector((state: ApplicationState) => state.tariff.trafficAreasGeography);

    useEffect(() => {
        const polygons = preparePolygons(props.trafficAreas);
        setLeafletPolygons(polygons);
    }, [props.trafficAreas, trafficAreasGeography]); // eslint-disable-line react-hooks/exhaustive-deps

    const getTrafficAreaCoordinates = (item: number): Coordinates => {
        const itemGeometry = trafficAreasGeography?.find((t) => t.externalId === item)?.geography;

        if (!itemGeometry)
            return [];

        let itemCoordinates: Position[] = [];

        if (itemGeometry.type === 'Polygon') {
            itemCoordinates = itemGeometry.coordinates.flat(1);
        } else if (itemGeometry.type === 'MultiPolygon') {
            itemCoordinates = itemGeometry.coordinates.flat(2);
        }

        return itemCoordinates.map(c => ({ latitude: c[1], longitude: c[0] }));
    };

    const findTrafficArea = (item: number): Coordinates[] | undefined => {
        if (!trafficAreasGeography)
            return undefined;

        if (item === TrafficAreaEnum.All) {
            return trafficAreasGeography.map(t => getTrafficAreaCoordinates(t.externalId));
        }

        return [getTrafficAreaCoordinates(item)];
    };

    const preparePolygons = (trafficAreas: number[]): LatLngExpression[][][] => {
        const result: LatLngExpression[][][] = [];

        trafficAreas.forEach(area => {
            const coords = findTrafficArea(area);
            if (!coords)
                return;

            coords.forEach(polygon => {
                const positions: LatLngExpression[] = polygon
                    .filter(isValidLatLng)
                    .map(c => [c.latitude, c.longitude]);
                if (positions.length > 0)
                    result.push([positions]);
            });
        });

        return result;
    };

    return (
        <div ref={mapWrapperRef} className="leaflet-map-wrapper">
            <LeafletMap
                center={[SPLIT_COORDINATE.lat, SPLIT_COORDINATE.lng]}
                zoom={12.9}
                style={props.fullView ? containerStyleFullView : containerStyle}
                attributionControl={false}
                zoomControl={false}
                ref={(mapRef: any) => {
                    setMapInstance(mapRef ? mapRef.leafletElement : null);
                }}
            >
                <LeafletBaseLayers activeBaseLayer={activeBaseLayer} labelsByLayer={labelsByLayer} />
                <LeafletFullscreenControl map={mapInstance} fullscreenElement={mapWrapperRef.current} />
                <ZoomControl position="bottomright" />
                <BoundsController map={mapInstance} polygons={leafletPolygons} />
                {leafletPolygons.map((rings, index) => (
                    <Polygon
                        key={index}
                        positions={rings as LatLngExpression[][]}
                        color={polygonStrokeColor}
                        fillColor={polygonFillColor}
                        fillOpacity={0.4}
                        weight={2}
                    />
                ))}
            </LeafletMap>
            <LeafletBaseLayerToggle
                activeLayer={activeBaseLayer}
                labelsByLayer={labelsByLayer}
                onLayerChange={setActiveBaseLayer}
                onLabelsChange={(layer, enabled) =>
                    setLabelsByLayer((current) => ({ ...current, [layer]: enabled }))
                }
            />
        </div>
    );
}

export default React.memo(MapJLSLeaflet);
